<template>
    <div id="step1">
        <div class="text-center text-xl my-12" v-if="loading && !error">
            <font-awesome-icon spin icon="spinner" size="2x"></font-awesome-icon>
            <p>Please wait while we gather your photos. This could take up to a minute...</p>
            <p v-if="loadTime > 30">Finishing up...</p>
        </div>

        <div class="text-center text-red-500" v-else-if="!loading && error">
            There was an error while gathering the photos. Please go back and try again.
        </div>

        <div v-else>
            <div v-if="order_data.poses && order_data.poses.length > 0">
                <HeadFoot title="Favorites" :disableBack="disableBack" v-on:back="back">
                    <button
                        type="button"
                        class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-leonard-green border-leonard-green text-leonard-white hover:bg-leonard-green-hover hover:border-transparent focus:ring-leonard-green cursor-pointer disabled:bg-leonard-green-disabled disabled:text-leonard-green disabled:cursor-not-allowed"
                        v-if="favorite_count > 0"
                        aria-label="Continue to Next Step"
                        @click="next"
                    >
                        Continue
                        <font-awesome-icon :icon="['fas', 'arrow-right']" class="ml-1 hidden md:inline-block"></font-awesome-icon>
                    </button>
                    <p v-if="favorite_count === 0" class="text-gray-700 ml-2" role="alert">
                        Please choose at least one (1) favorite before continuing
                    </p>
                </HeadFoot>

                <div class="font-bold text-center text-lg mt-8">All Poses</div>
                <p class="text-center italic text-sm">
                    Click on any heart to favorite the pose.
                    <br>
                    Click the pose image to open a larger preview.
                    <br>
                    Selecting your favorites has no effect on the yearbook selection.
                </p>

                <Alert align="left" title="No Favorites Chosen" type="warning" width="50" class="mt-8" v-if="favorite_count < 1">
                    Please choose at least one (1) favorite from the poses below to continue to view packages.
                </Alert>

                <div class="grid md:grid-cols-2 md:gap-4">
                    <div class="md:p-1">
                        <Alert align="center" title="Notice" type="info" width="100">
                            All formal photos will be printed on the default traditional <span class="cursor-pointer underline text-blue-400" data-toggle="modal" data-target="#ja_modal">JA background</span>.
                        </Alert>
                    </div>
                    <div class="md:p-1">
                        <Alert align="center" title="Important!" type="info" width="100">
                            If you took any casual photos inside or outside at our studio or on your campus, those may be delayed and ready at a later date. You will be notified when they are ready to view.
                        </Alert>
                    </div>
                </div>

                <div class="grid grid-flow-row grid-cols-2 gap-4 mt-4 selections sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
                    <div class="relative mb-4" v-for="(pic, index) in order_data.poses" :key="index">
                        <figure class="clickable" :class="{favorited: pic.favorite === true || pic.favorite === 1}">
                            <div class="img-wrapper" :class="{'processing': processing === index}" data-toggle="modal" data-target="#magnify_modal" >
                                <img :src="pic.image_path || '/images/default.png'" :alt="parseInt(pic.FLM_ORD_NO).toString().slice(-2) + '-' + parseInt(pic.FRM).toString().padStart(3, '0')" style="width: 100%;" @click="magnify($event, pic.FLM_ORD_NO, pic.FRM, index)">
                                <font-awesome-icon spin icon="spinner" size="2x" class="text-white" v-if="processing === index"></font-awesome-icon>
                                <div class="absolute bottom-3 w-full text-center text-white font-bold leading-none text-shadow">{{parseInt(pic.FLM_ORD_NO).toString().slice(-2) + '-' + parseInt(pic.FRM).toString().padStart(3, '0')}}</div>
                            </div>
                            <figcaption class="text-center text-sm">
                                <span class="italic leading-none" v-if="isFormal(pic)">Formal Selection</span>
                                <span class="italic leading-none" v-if="isCasual(pic)">Casual Selection</span>
                            </figcaption>
                        </figure>
                        <div
                            class="formal-icon absolute py-2 px-3 top-0 right-0"
                            :class="{'admin-selection bg-blue-500': adminSelection(pic, 'formal') && !studentSelection(pic, 'formal'), 'student-selection bg-green-500': studentSelection(pic, 'formal') && !adminSelection(pic, 'formal'), 'student-selection bg-teal-400': studentSelection(pic, 'formal') && adminSelection(pic, 'formal')}">
                            <div class="admin text-white" v-if="adminSelection(pic, 'formal') && !studentSelection(pic, 'formal')">
                                <font-awesome-icon :icon="['fas', 'user-tie']"></font-awesome-icon>
                            </div>
                            <div class="student text-white" v-if="studentSelection(pic, 'formal')">
                                <font-awesome-icon :icon="['fas', 'user-tie']"></font-awesome-icon>
                            </div>
                        </div>
                        <div
                            class="casual-icon absolute py-2 px-3 top-0 right-0"
                            :class="{'admin-selection bg-blue-500 ': adminSelection(pic, 'casual') && !studentSelection(pic, 'casual'), 'student-selection bg-green-500': studentSelection(pic, 'casual') && !adminSelection(pic, 'casual'), 'student-selection bg-teal-400': studentSelection(pic, 'casual') && adminSelection(pic, 'casual')}">
                            <div class="admin text-white" v-if="adminSelection(pic, 'casual') && !studentSelection(pic, 'casual')">
                                <font-awesome-icon :icon="['fas', 'user']"></font-awesome-icon>
                            </div>
                            <div class="student text-white" v-if="studentSelection(pic, 'casual')">
                                <font-awesome-icon :icon="['fas', 'user']"></font-awesome-icon>
                            </div>
                        </div>

                        <font-awesome-icon fixed-width :icon="['fas', 'heart']" class="h-6 w-6 md:h-8 md:w-8 text-red-600 favorite" size="2x" v-show="pic.favorite" @click="toggleFavorite(pic.FLM_ORD_NO, pic.FRM, index)"></font-awesome-icon>
                        <font-awesome-icon fixed-width :icon="['far', 'heart']" class="h-6 w-6 md:h-8 md:w-8 text-white favorite" size="2x" v-show="!pic.favorite" @click="toggleFavorite(pic.FLM_ORD_NO, pic.FRM, index)"></font-awesome-icon>
                    </div>
                </div>

                <HeadFoot :disableBack="disableBack" v-on:back="back">
                    <button
                        type="button"
                        class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-leonard-green border-leonard-green text-leonard-white hover:bg-leonard-green-hover hover:border-transparent focus:ring-leonard-green cursor-pointer disabled:bg-leonard-green-disabled disabled:text-leonard-green disabled:cursor-not-allowed"
                        v-if="favorite_count > 0"
                        aria-label="Continue to Next Step"
                        @click="next"
                    >
                        Continue
                        <font-awesome-icon :icon="['fas', 'arrow-right']" class="ml-1 hidden md:inline-block"></font-awesome-icon>
                    </button>
                    <p v-if="favorite_count === 0" class="text-gray-700 ml-2" role="alert">
                        Please choose at least one (1) favorite before continuing
                    </p>
                </HeadFoot>
            </div>

            <div class="data-loading text-center mt-5" v-else>
                <font-awesome-icon icon="spinner" size="3x" pulse></font-awesome-icon>
            </div>

            <!-- Modal for displaying the full size image preview -->
            <div class="modal fade" id="magnify_modal">
                <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content border-0 bg-transparent">
                        <div class="modal-body text-center" v-if="order_data.poses && order_data.poses.length > 0">
                            <figure id="fullsize-image" class="clickable relative">
                                <img class="border border-white w-full" :src="magnification.src" :alt="parseInt(magnification.flm).toString().slice(-2) + '-' + parseInt(magnification.frm).toString().padStart(3, '0')" data-dismiss="modal">
                                <font-awesome-icon :icon="['fas', 'heart']" size="3x" class="w-4 mr-1 favorite text-red-600 hidden sm:block" v-show="order_data.poses[magnification.poses_index].favorite" @click="toggleFavorite(magnification.flm, magnification.frm, magnification.poses_index)"></font-awesome-icon>
                                <font-awesome-icon :icon="['far', 'heart']" size="3x" class="w-4 mr-1 favorite text-gray-600 hidden sm:block" v-show="!order_data.poses[magnification.poses_index].favorite" @click="toggleFavorite(magnification.flm, magnification.frm, magnification.poses_index)"></font-awesome-icon>
                                <figcaption class="absolute left-0 right-0 bottom-2 text-white font-bold text-xl">
                                    {{parseInt(magnification.flm).toString().slice(-2) + '-' + parseInt(magnification.frm).toString().padStart(3, '0')}}
                                </figcaption>
                            </figure>
                            <button type="button" class="btn text-lg text-white bg-red-600 border border-red-700 hover:bg-red-500 hover:border-red-600 text-center my-2" @click="toggleFavorite(magnification.flm, magnification.frm, magnification.poses_index)" aria-label="Favorite This Pose">
                                <font-awesome-icon :icon="['fas', 'heart']" class="w-4 mr-1" v-show="order_data.poses[magnification.poses_index].favorite"></font-awesome-icon>
                                <font-awesome-icon :icon="['far', 'heart']" class="w-4 mr-1" v-show="!order_data.poses[magnification.poses_index].favorite"></font-awesome-icon>
                                {{order_data.poses[magnification.poses_index].favorite ? 'Unfavorite' : 'Favorite'}}
                            </button>
                            <button type="button" class="btn text-lg text-white bg-green-600 border border-green-700 hover:bg-green-500 hover:border-green-600 text-center my-2" @click="rotateImage($event)" aria-label="Rotate Pose Preview">
                                <font-awesome-icon :icon="['fas', 'arrow-rotate-left']" class="w-4 mr-1" rotate="180"></font-awesome-icon> Rotate
                            </button>
                            <button type="button" class="btn text-lg text-white bg-blue-600 border border-blue-700 hover:bg-blue-500 hover:border-blue-600 text-center my-2" data-dismiss="modal" aria-label="Close Pose Preview">
                                <font-awesome-icon :icon="['fas', 'xmark']" class="w-5 mr-1"></font-awesome-icon> Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Modal for showing unfavorite error -->
            <div class="modal fade" id="unfavorite_error_modal">
                <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content">
                        <div class="modal-body">
                            <p>At this step in the process you cannot unfavorite unless you want to reset all data. Please note that this will remove any poses chosen on packages previously viewed.</p>
                        </div>
                        <div class="bg-gray-100 text-right py-4 px-2 rounded-b-md">
                            <button
                                type="button"
                                class="btn text-gray-700 border border-gray-600 hover:bg-gray-600 hover:text-white"
                                data-dismiss="modal"
                                @click="cancelUnfavorite()"
                            >Cancel</button>
                            <button
                                type="button"
                                class="btn px-6 text-white border border-blue-500 hover:border-blue-700 bg-blue-500 hover:bg-blue-700 disabled:opacity-75"
                                data-dismiss="modal"
                                @click="resetAndContinue()"
                                :disabled="resetting"
                            >Reset &amp; Unfavorite</button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- JA Background Modal -->
            <div class="modal fade" id="ja_modal">
                <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content border-0 bg-transparent">
                        <div class="modal-body text-center">
                            <figure id="fullsize-image" class="clickable relative">
                                <img class="border border-white w-full" src="/images/ja.jpg" alt="Traditional JA Background" data-dismiss="modal">
                                <figcaption class="absolute left-0 right-0 bottom-2 text-white font-bold text-xl">
                                    Traditional JA Background Preview
                                </figcaption>
                            </figure>
                            <button type="button" class="btn text-lg text-white bg-blue-600 border border-blue-700 hover:bg-blue-500 hover:border-blue-600 text-center my-2" data-dismiss="modal" aria-label="Close Pose Preview">
                                <font-awesome-icon :icon="['fas', 'xmark']" class="w-5 mr-1"></font-awesome-icon> Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
    import { library } from '@fortawesome/fontawesome-svg-core';
    import { faSquare, faArrowRight, faExchangeAlt, faPlus, faHeart, faUser, faUserTie, faSpinner, faXmark, faArrowRotateLeft } from '@fortawesome/free-solid-svg-icons';
    import { faHeart as farHeart } from '@fortawesome/free-regular-svg-icons';

    import { lightFormat, parse, isAfter, getYear, getMonth, endOfDay } from 'date-fns';
    import Order from '../../classes/Order';
    import Photo from '../../classes/Photo';
    import Setting from '../../classes/Setting';

    import Alert from '../../components/app/Alert';
    import HeadFoot from '../ordersteps/partials/StepHeaderFooter';

    export default {
        props: ['disableBack'],

        data() {
            return {
                Order: new Order(),
                Photo: new Photo(),
                Setting: new Setting(),
                access_code: localStorage.getItem('access_code'),
                processing: null,
                unfavoriting: null,
                order_data: {},
                change: {
                    type: 'formal',
                    available: [],
                    current: {}
                },
                casualPose: {
                    show: false,
                    src: '',
                    flm: '',
                    frm: '',
                },
                formalPose: {
                    set: false,
                    src: '',
                    flm: '',
                    frm: '',
                },
                magnification: {
                    src: '',
                    flm: '',
                    frm: '',
                    poses_index: 0,
                },
                favorite_count: 0,
                resetting: false,
                saving: false,
                loading: false,
                error: false,
                loadTime: 0,
                timer: null,
                // sortFavorites: this.$route.query.sort
                isAfter, lightFormat, parse, getYear, getMonth, endOfDay
            }
        },

        components: {
            Alert,
            HeadFoot,
        },

        created() {
            library.add(faSpinner, faSquare, faArrowRight, faExchangeAlt, faPlus, faHeart, farHeart, faUser, faUserTie, faXmark, faArrowRotateLeft);

            if(this.access_code != null) {
                let self = this;
                let order = new Order();

                let data = {
                    access_code: this.access_code,
                    user_id: this.$auth.user().details.id
                };

                window.events.$emit('finishedLoading');
                this.startTimer()
                order.checkPhotos(data).then(function(response) {
                    let data = response.data;

                    self.endTimer()

                    if(data.message) {
                        feedback(data.message, 'danger');
                        self.$router.push({params: {step: null}});
                    } else {
                        let photos = data.photos;

                        localStorage.setItem('con_no', photos[0].CON_NO);

                        // sort photos by favorite status
                        photos.sort((a,b) => a.favorite ? -1 : b.favorite ? 1 : 0);

                        // count favorites
                        self.favorite_count = photos.filter(pic => {
                            return pic.favorite === true || pic.favorite === 1;
                        }).length;

                        self.order_data = {
                            poses: photos
                        };

                        self.setFormalPose(photos);
                        self.setCasualPose(photos);
                    }
                })
            }
        },

        mounted() {
            // KEEP FOR WHEN WE REMOVE JQUERY
            // let self = this;
            // this.$el.querySelector('#magnify_modal').addEventListener('hidden.bs.modal', e => {
            //     console.log(e);
            //     self.$el.querySelector('#fullsize-image').classList.remove('rotate');
            //     self.$el.querySelector('#fullsize-image').parentElement.querySelector('.btn-success .fas').classList.add('fa-flip-horizontal');
            // });

            window.events.$emit('updateProgress')

            $('#magnify_modal').on('hidden.bs.modal', function() {
                $('#fullsize-image').removeClass('rotate').siblings('.btn-success').children('.fas').addClass('fa-flip-horizontal');
            });

            document.title = 'Favorites - ' + document.title;
        },

        methods: {
            startTimer() {
                let self = this;
                this.loading = true;

                let start = Date.now();
                this.timer = setInterval(function() {
                    let delta = Date.now() - start; // milliseconds elapsed since start
                    self.loadTime = Math.floor(delta / 1000); // in seconds
                }, 1000);
            },

            endTimer() {
                this.loading = false;
                this.timer = null
            },

            validDate(d) {
                //let parsed = this.parse(d, 'MM/dd/yyyy hh:mm:ss a', new Date());
                let parsed = this.parse(d, 'yyyy-MM-dd HH:mm:ss', new Date());

                if(parsed instanceof Date && isNaN(parsed.getTime())) {
                    console.warn("Invalid date, attempting to reparse...");
                    parsed = this.parse(d, 'yyyy-MM-dd', new Date());
                }

                return parsed;
            },

            setFormalPose(photos) {
                let self = this;

                photos.some(function(photo) {
                    let poseObj = {
                        set: true,
                        image_path: photo.image_path,
                        FLM_ORD_NO: photo.FLM_ORD_NO,
                        FRM: photo.FRM,
                        student: false,
                    };

                    if(photo.YBE1 === 1 && photo.YBKPOSE2 === 1) {
                        self.formalPose = poseObj;
                    }

                    if(photo.YBE1 === 1 && photo.YBKPOSE === 1) { // Prioritize Student Selection
                        poseObj.student = true;
                        self.formalPose = poseObj;

                        return true;
                    }
                });
            },

            setCasualPose(photos) {
                let self = this;

                let poseObj = {
                    show: false,
                    image_path: null,
                    FLM_ORD_NO: null,
                    FRM: null,
                    student: false,
                };

                photos.some(function(photo) {
                    if(photo.YBE2 === 1) {
                        poseObj.show = true;
                    }

                    if(photo.YBE2 === 1 && photo.YBKPOSE4 === 1) {
                        poseObj.image_path = photo.image_path
                        poseObj.FLM_ORD_NO = photo.FLM_ORD_NO
                        poseObj.FRM = photo.FRM
                        poseObj.student = false;
                    }

                    if(photo.YBE2 === 1 && photo.YBKPOSE3 === 1) { // Prioritize Student Selection
                        poseObj.image_path = photo.image_path
                        poseObj.FLM_ORD_NO = photo.FLM_ORD_NO
                        poseObj.FRM = photo.FRM
                        poseObj.student = true;
                        return true;
                    }
                });

                self.casualPose = poseObj;
            },

            openChangePose(type) {
                let filter = (type === 'casual') ? 'YBE2' : 'YBE1';
                let availablePoses = [];

                this.order_data.poses.forEach(function(photo) {
                    if(photo[filter]) {
                        availablePoses.push(photo);
                    }
                });

                let currentPose = (type === 'casual') ? this.casualPose : this.formalPose;

                this.change = {
                    type: type,
                    available: availablePoses,
                    current: currentPose
                }
            },

            updatePose(poseType, changedPose, ybk) {
                let poses = this.order_data.poses;
                let index = poses.findIndex(pose => pose.FLM_ORD_NO === changedPose.FLM_ORD_NO && pose.FRM === changedPose.FRM);
                let ybkpose = this.isEmployee ? ybk[1] : ybk[0];

                if(index !== -1) {
                    poses.forEach(pose => {
                        pose[ybkpose] = 0;
                    });

                    poses[index][ybkpose] = 1;

                    // only change pose if there is not a student selection
                    // or this is not an employee
                    if(poseType === 'casual' && (!this.isEmployee || this.isEmployee && !this.casualPose.student)) {
                        this.casualPose = {
                            show: true,
                            image_path: changedPose.image_path,
                            FLM_ORD_NO: changedPose.FLM_ORD_NO,
                            FRM: changedPose.FRM
                        }
                    }

                    if(poseType === 'formal' && (!this.isEmployee || this.isEmployee && !this.formalPose.student)) {
                        this.formalPose = {
                            set: true,
                            image_path: changedPose.image_path,
                            FLM_ORD_NO: changedPose.FLM_ORD_NO,
                            FRM: changedPose.FRM
                        }
                    }
                }
            },

            toggleFavorite(flm, frm, i) {
                this.processing = i;

                let poses = this.order_data.poses;
                let index = poses.findIndex(pose => pose.FLM_ORD_NO === flm && pose.FRM === frm);

                if(index !== -1) {
                    poses[index].favorite === true || poses[index].favorite === 1 ? this.unfavorite(index) : this.favorite(index);
                } else {
                    feedback('Something went wrong, please refresh the page.', 'danger');
                }
            },

            favorite(index) {
                let self = this;
                let photo = new Photo();
                let pose = this.order_data.poses[index];
                let data = {
                    con_no: pose.CON_NO,
                    user_id: this.$auth.user().details.id
                };

                photo.favorite(pose.FLM_ORD_NO, pose.FRM, data).then(resp => {
                    self.processing = null;
                    if(!resp.success) {
                        feedback('An error occurred while favoriting the photo. Please try again later.', 'danger');
                        return;
                    }

                    pose.favorite = 1;
                    self.favorite_count++;
                    feedback('Favorite Added', 'success');
                });
            },

            unfavorite(index) {
                let self = this;
                let photo = new Photo();
                let pose = this.order_data.poses[index];
                let data = {
                    con_no: pose.CON_NO,
                    user_id: this.$auth.user().details.id
                };

                // if the pose is used in package selection, do not unfavorite - show message
                if(this.checkUsedInPackageSelection(pose)) {
                    this.unfavoriting = pose;
                    $('#unfavorite_error_modal').modal('show');
                    return;
                }

                photo.unfavorite(pose.FLM_ORD_NO, pose.FRM, data).then(resp => {
                    self.processing = null;
                    self.unfavoriting = null;
                    if(!resp.success) {
                        feedback('An error occurred while removing the favorite. Please try again later.', 'danger');
                        return;
                    }

                    pose.favorite = 0;
                    self.favorite_count--;
                    feedback('Favorite Removed', 'success');
                });
            },

            checkUsedInPackageSelection(pose) {
                let pkgList = ['001', '002', '003', '004', '005', '006', '007'];
                for(const num of pkgList) {
                    let pkg = localStorage.getItem(this.access_code + '-' + num);
                    if(pkg) {
                        let pkgData = JSON.parse(pkg);
                        for(const sheetType of pkgData) {
                            let exists = sheetType.sheetPoses.filter(obj => obj.film === pose.FLM_ORD_NO && obj.frame === pose.FRM);
                            if(exists.length > 0) {
                                return true;
                            }
                        }
                    }
                }
                return false;
            },

            resetAndContinue() {
                this.resetting = true;
                let pkgList = ['001', '002', '003', '004', '005', '006', '007'];
                for(const num of pkgList) {
                    let pkg = localStorage.getItem(this.access_code + '-' + num);
                    if(pkg) {
                        localStorage.removeItem(this.access_code + '-' + num);
                    }
                }

                this.resetting = false;
                this.toggleFavorite(this.unfavoriting.FLM_ORD_NO, this.unfavoriting.FRM, this.processing);
            },

            cancelUnfavorite() {
                this.processing = null;
                this.unfavoriting = null;
                // feedback('Cannot unfavorite a pose included in saved package data.', 'danger');
            },

            magnify(event, flm, frm, i) {
                this.magnification = {
                    src: $(event.target).attr('src'),
                    flm: flm,
                    frm: frm,
                    poses_index: i
                }
            },

            rotateImage(event) {
                $('#fullsize-image').toggleClass('rotate');
                $(event.target).children('.fas').toggleClass('fa-flip-horizontal');
            },

            isFormal: function(pose) {
                return pose.FRM === this.formalPose.FRM && pose.FLM_ORD_NO === this.formalPose.FLM_ORD_NO
            },

            isCasual: function(pose) {
                return pose.FRM === this.casualPose.FRM && pose.FLM_ORD_NO === this.casualPose.FLM_ORD_NO
            },

            adminSelection: function(pose, type) {
                return ((type === 'formal' && parseInt(pose.YBKPOSE2) === 1) || (type === 'casual' && parseInt(pose.YBKPOSE4) === 1)) && this.isEmployee;
            },

            studentSelection: function(pose, type) {
                return ((type === 'formal' && parseInt(pose.YBKPOSE) === 1) || (type === 'casual' && parseInt(pose.YBKPOSE3) === 1));
            },

            next() {
                if(!this.$route.query.return) {
                    this.$emit('nextStep');
                    return;
                }

                this.$emit('nextStep', {
                    step: this.$route.query.return,
                    pkg: this.$route.query.pkg,
                });
            },

            back() {
                this.$emit('back', 'yearbook');
            }
        },
    }
</script>

<style lang="scss">
    .title {
        position: absolute;
        top: -65px;
        background: #000;
        border-radius: 4px;
        color: #FFF;
        padding: 0.35rem 0.5rem;
        width: 175px;
        left: calc(50% - 87.5px);
        font-size: small;
        &::after {
            content: '';
            position: absolute;
            bottom: -20px;
            left: calc(50% - 10px);
            border: 10px transparent solid;
            border-top: 10px #000 solid;
        }
    }
</style>

<style lang="scss" scoped>
    .text-shadow {
        text-shadow: 0 0 4px #000;
    }

    .btn[disabled], .btn.disabled {
        cursor: not-allowed;
    }

    .modal-content {
        box-shadow: none;
        figure {
            img, svg, figcaption {
                transition: all 400ms ease-in-out;
            }

            &.rotate {
                img {
                    transform: rotate(90deg);
                }
                svg {
                    /*transform: translate(350px, 70px);*/
                    transform: translate(30rem, 6rem)
                }
                figcaption {
                    transform: translateY(-45px);
                }
            }
        }
    }

    .favorite {
        position: absolute;
        top: 0.5rem;
        left: 0.25rem;
        filter: drop-shadow(1px 1px 1px #000);
    }

    .clickable {
        cursor: pointer;
    }
    .unclickable {
        cursor: not-allowed;
    }

    figure {
        img {
            border-radius: 1rem;
        }

        small {
            position: absolute;
            bottom: -20px;
            left: 0;
            right: 0;
            text-align: center;
        }

        .photo-button {
            position: absolute;
            top: 10px;
            right: -20px;
            width: 40px;
            height: 40px;
            border-radius: 50%;
        }
    }

    .selections {
        figure {
            position: relative;

            figcaption {
                position:relative;
                bottom: 0;
                color: black;
                text-shadow: none;
            }

            .img-wrapper {
                display:block;
                position:relative;
                &.processing {
                    position: relative;
                    &::before, &::after {
                        position: absolute;
                        content: '';
                    }
                    &::before {
                        background: rgba(0,0,0,0.5);
                        top: 0;
                        right: 0;
                        left: 0;
                        bottom: 0;
                        border-radius: 1rem;
                    }

                    .fa-spinner {
                        position: absolute;
                        top: calc(50% - 14px);
                        left: calc(50% - 14px);
                    }
                }
            }

            img {
                box-shadow: none;
            }

        }
    }

    #magnify_modal {
        figure {
            .favorite {
                left: 10px;
            }

            figcaption {
                text-shadow: 0 0 6px #000;
            }
        }
    }

    .formal-icon, .casual-icon {
        &.admin-selection.student-selection svg.admin {
            position: relative;
            right: 32px;
        }
    }

    @media screen and (min-width: 991.98px) {
        figure {
            margin-top: 0;

            img {
                width: 100%;
            }
        }

        #magnify_modal {
            figure .favorite {
                left: 10px;
            }
        }
    }
</style>
